import {
  GeneralLedgerTransaction,
  Response,
  apiAbortController,
  AccountBalance,
  AccountType,
  ChangeLog,
  Document,
  DocumentStatus,
  DocumentType,
} from '@ui/src';
import api from '../../../../api/api';
import { UploadResponse } from '../../types';

export const statementsItemApi = api.injectEndpoints({
  endpoints: (build) => ({
    getGeneralLedgerStatment: build.query<
      { data: GeneralLedgerTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/general-ledger/transactions/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),

    getAccountBalanceById: build.query<
      Response<AccountBalance>,
      {
        id: string;
        query?: string;
        typeAcc: AccountType.CREDIT | AccountType.DEBIT;
      }
    >({
      query: (data) => ({
        url: `/v1/accounts/${data.id}/balances?all=true${data.query || ''}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getStatementChangeLogs: build.query<Response<ChangeLog>, string>({
      query: (query) => ({
        url: `/v1/audit-log${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteStatementDocuments: build.mutation<
      {
        data: { count: number };
      },
      {
        id: string;
        body: {
          ids: string[];
        };
      }
    >({
      query: (data) => ({
        url: `/v1/general-ledger/transactions/${data.id}/documents/batch-delete`,
        method: 'DELETE',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getStatementsGLDocuments: build.query<
      Response<Document>,
      { id: string; cursor?: string }
    >({
      query: (data) => ({
        url: `/v1/general-ledger/transactions/${data.id}/documents${
          data.cursor ? `?cursor=${data.cursor}` : ''
        }`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getStatementsGLDocument: build.query<
      { data: Document },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/general-ledger/transactions/${data.id}/documents/${data.documentId}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addStatementDocumentToGLTransaction: build.mutation<
      { data: Document },
      {
        id: string;
        body: {
          name: string;
          uploadId: string;
        };
      }
    >({
      query: (data) => ({
        url: `/v1/general-ledger/transactions/${data.id}/documents`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteStatementsGLDocuments: build.mutation<
      {
        data: { count: number };
      },
      {
        id: string;
        body: {
          ids: string[];
        };
      }
    >({
      query: (data) => ({
        url: `/v1/general-ledger/transactions/${data.id}/documents/batch-delete`,
        method: 'DELETE',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    checkStatementDocumentStringName: build.mutation<any, { value: string }>({
      query: (data) => ({
        url: '/v1/swift/check-string',
        method: 'POST',
        body: data,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    statementsUpload: build.mutation({
      async queryFn(file: File, _queryApi, _extraOptions, fetchWithBQ) {
        const formData = new FormData();
        formData.append('files', file);

        const response = (await fetchWithBQ({
          url: '/v1/uploads',
          method: 'POST',
          body: formData,
        })) as { data: UploadResponse[] };

        return response;
      },
    }),
    getStatementsDocumentFile: build.query<
      { data: any },
      { id: string; documentId: string }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents/${data.documentId}/file`,
        responseHandler: (response) => response.blob(),
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeStatementsDocumentStatus: build.mutation<
      { data: { count: number } },
      { id: string; body: { ids: string[]; status: DocumentStatus } }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents/batch-update-status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeStatementsDocumentCategory: build.mutation<
      { data: { count: number } },
      { id: string; body: { ids: string[]; category: DocumentType } }
    >({
      query: (data) => ({
        url: `/v1/transactions/${data.id}/documents/batch-update-category`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addStatementsComment: build.mutation<
      any,
      { id: string; body: { text: string } }
    >({
      query: (data) => ({
        url: `/v1/documents/${data.id}/comments`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    editStatementsComment: build.mutation<
      any,
      { id: string; commentId: string; body: { text: string } }
    >({
      query: (data) => ({
        url: `/v1/documents/${data.id}/comments/${data.commentId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteStatementsComment: build.mutation<
      any,
      { id: string; commentId: string }
    >({
      query: (data) => ({
        url: `/v1/documents/${data.id}/comments/${data.commentId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetGeneralLedgerStatmentQuery,
  useLazyGetAccountBalanceByIdQuery,
  useLazyGetStatementChangeLogsQuery,
  useDeleteStatementDocumentsMutation,
  useAddStatementDocumentToGLTransactionMutation,
  useDeleteStatementsGLDocumentsMutation,
  useLazyGetStatementsGLDocumentQuery,
  useLazyGetStatementsGLDocumentsQuery,
  useCheckStatementDocumentStringNameMutation,
  useStatementsUploadMutation,
  useLazyGetStatementsDocumentFileQuery,
  useChangeStatementsDocumentStatusMutation,
  useChangeStatementsDocumentCategoryMutation,
  useAddStatementsCommentMutation,
  useEditStatementsCommentMutation,
  useDeleteStatementsCommentMutation,
} = statementsItemApi;

export const {
  endpoints: {
    getGeneralLedgerStatment,
    getAccountBalanceById,
    getStatementChangeLogs,
    getStatementsGLDocument,
    getStatementsGLDocuments,
    addStatementDocumentToGLTransaction,
    deleteStatementsGLDocuments,
    checkStatementDocumentStringName,
    statementsUpload,
    getStatementsDocumentFile,
    changeStatementsDocumentStatus,
    changeStatementsDocumentCategory,
    addStatementsComment,
    editStatementsComment,
    deleteStatementDocuments,
    deleteStatementsComment,
  },
} = statementsItemApi;
